import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCompanyInfo } from '../lib/cms';
import { imageUrl } from '../lib/images';

export default function Navbar() {
  const companyInfo = useCompanyInfo();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on page navigate
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const menuItems = [
    { label: "Hjem", href: "/" },
    {
      label: "Erhverv & privat",
      href: "/erhverv-privat",
      children: [
        {
          label: "Hegn",
          href: "/hegn",
          icon: "fa-fence",
          desc: "Kvalitetshegn til have og erhverv",
          items: [
            { label: "Komposithegn", href: "/komposithegn" },
            { label: "Industrihegn", href: "/industrihegn" },
            { label: "Støjhegn", href: "/stoejhegn" },
            { label: "Panelhegn", href: "/panelhegn" },
            { label: "Smedejernshegn", href: "/smedejernshegn" },
            { label: "Rionet", href: "/rionet" },
            { label: "Betonhegn", href: "/betonhegn" },
            { label: "Træbeklædt hegn", href: "/traebeklaedt-hegn-et-naturligt-valg" },
          ]
        },
        {
          label: "Låger",
          href: "/laager",
          icon: "fa-door-open",
          desc: "Flotte låger tilpasset dit hegn",
          items: [
            { label: "Kompositlåge", href: "/kompositlaage" },
            { label: "Kompositdør", href: "/kompositdor" },
            { label: "Panellåge", href: "/panellaage" },
            { label: "Rionetlåge", href: "/rionetlaage" },
            { label: "Smedejernslaage", href: "/smedejernslaage" },
            { label: "Træbeklædte låger", href: "/traebeklaedte-laager" },
          ]
        },
        {
          label: "Porte",
          href: "/porte",
          icon: "fa-warehouse",
          desc: "Sikre og funktionelle porte",
          items: [
            { label: "Skydeporte", href: "/skydeporte" },
            { label: "Dobbeltporte", href: "/dobbeltporte" },
            { label: "Foldeporte", href: "/foldeporte-i-funktionelt-design" },
          ]
        },
        {
          label: "Terrasser",
          href: "/terrasser",
          icon: "fa-layer-group",
          desc: "Terrasseløsninger og afskærmning",
          items: [
            { label: "Terrasser", href: "/terrasser" },
            { label: "Træterrasse", href: "/traeterrasse" },
            { label: "Kompositterrasse", href: "/kompositterrasse" },
            { label: "Glasafskærmning", href: "/terrasser" },
          ]
        },
        {
          label: "Carport & Skur",
          href: "/carport",
          icon: "fa-car",
          desc: "Stålcarporte og skræddersyede skure",
          items: [
            { label: "Carport", href: "/carport" },
            { label: "Skur", href: "/skur" },
            { label: "Galleri: Skur", href: "/galleri/skur" },
          ]
        },
        {
          label: "Facader & Vinduer",
          href: "/facader",
          icon: "fa-building",
          desc: "Kvalitetsfacader og vinduespartier",
          items: [
            { label: "Facader", href: "/facader" },
            { label: "Vinduespartier", href: "/vinduespartier" },
          ]
        },
      ]
    },
    { label: "Om Hovendals", href: "/om-hovendals" },
    { label: "Projekter", href: "/projekter" },
    { label: "Referencer", href: "/referencer" },
    { label: "Kontakt", href: "/kontakt" }
  ];

  const isErhvervPrivatActive = [
    '/erhverv-privat',
    '/hegn', '/komposithegn', '/stoejhegn', '/panelhegn', '/smedejernshegn', '/rionet', '/betonhegn', '/traebeklaedt-hegn-et-naturligt-valg',
    '/laager', '/kompositlaage', '/kompositdor', '/kompositdoer', '/panellaage', '/rionetlaage', '/smedejernslaage', '/traebeklaedte-laager',
    '/porte', '/skydeporte', '/dobbeltporte', '/foldeporte-i-funktionelt-design',
    '/terrasser', '/kompositterrasse', '/traeterrasse',
    '/carport', '/skur', '/facader', '/vinduespartier'
  ].includes(location.pathname);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-200">
      {/* Top micro-bar in navy */}
      <div className="bg-[#143463] text-slate-200 text-xs py-2 px-4 border-b border-[#183E7F]/40">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex items-center gap-4 text-slate-200 font-light">
            <span className="flex items-center gap-1.5">
              <i className="fa-sharp fa-solid fa-badge-check text-[#C2CCDF]" aria-hidden="true"></i>
              <span>Kvalitetshåndværk til private og erhverv i hele Danmark</span>
            </span>
          </div>

          <div className="flex items-center gap-5 font-light">
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="hover:text-white transition-colors flex items-center gap-1.5"
            >
              <i className="fa-sharp fa-solid fa-phone text-[#C2CCDF]" aria-hidden="true"></i>
              <span>{companyInfo.phone}</span>
            </a>
            <span className="text-[#183E7F]">|</span>
            <a
              href={`mailto:${companyInfo.email}`}
              className="hover:text-white transition-colors flex items-center gap-1.5"
            >
              <i className="fa-sharp fa-solid fa-envelope text-[#C2CCDF]" aria-hidden="true"></i>
              <span>{companyInfo.email}</span>
            </a>
            <span className="text-[#183E7F]">|</span>
            <a
              href={companyInfo.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Følg Hovendals på Facebook"
              className="text-[#C2CCDF] hover:text-white transition-colors"
            >
              <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className={`transition-all duration-200 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-white py-4 border-b border-[#F3F4F7]'
      }`}>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none" aria-label="HOVENDALS ApS Forside">
            <img
              src={imageUrl(companyInfo.logo, 'width=420,quality=90,format=auto')}
              alt="HOVENDALS ApS"
              className="h-10 sm:h-12 w-auto object-contain transition-transform group-hover:scale-[1.01]"
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2">
            {menuItems.map((item) => {
              if (item.children) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(true)}
                    onMouseLeave={() => setDropdownOpen(false)}
                  >
                    <Link
                      to={item.href}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium transition-colors ${
                        isErhvervPrivatActive
                          ? 'text-[#004FA4] font-semibold'
                          : 'text-[#4B515D] hover:text-[#004FA4]'
                      }`}
                    >
                      <span>{item.label}</span>
                      <i className={`fa-sharp fa-solid fa-chevron-down text-xs transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
                    </Link>

                    {/* Dropdown Menu - Minimalist architectural grid */}
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 z-50">
                        <div className="w-[820px] bg-white border border-[#D6D3CE]/60 shadow-xl p-6">
                          <div className="flex items-center justify-between pb-3 mb-4 border-b border-[#F3F4F7]">
                            <Link
                              to={item.href}
                              className="text-xs font-semibold uppercase tracking-wider text-[#143463] hover:text-[#004FA4] flex items-center gap-2 group"
                            >
                              <span>Gå til samlet oversigt: {item.label}</span>
                              <i className="fa-sharp fa-solid fa-arrow-right text-xs text-[#004FA4] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                            </Link>
                            <span className="text-[11px] font-medium px-2 py-0.5 bg-[#F3F4F7] text-[#004FA4] border border-[#C2CCDF]/40">
                              Landsdækkende montage
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-6">
                            {item.children.map((sub) => (
                              <div key={sub.label} className="space-y-2">
                                <Link
                                  to={sub.href}
                                  className="flex items-center gap-2 text-sm font-medium text-[#143463] hover:text-[#004FA4] transition-colors group"
                                >
                                  <div className="w-6 h-6 bg-[#F3F4F7] group-hover:bg-[#004FA4]/10 text-[#004FA4] flex items-center justify-center shrink-0 transition-colors text-xs">
                                    <i className={`fa-sharp fa-solid ${sub.icon}`} aria-hidden="true"></i>
                                  </div>
                                  <span>{sub.label}</span>
                                </Link>

                                {sub.items && (
                                  <ul className="pl-8 space-y-1">
                                    {sub.items.map((subItem) => (
                                      <li key={subItem.href}>
                                        <Link
                                          to={subItem.href}
                                          className="text-xs text-[#4B515D] hover:text-[#004FA4] transition-colors block py-0.5"
                                        >
                                          {subItem.label}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-3.5 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-[#004FA4] font-semibold'
                      : 'text-[#4B515D] hover:text-[#004FA4]'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* Action CTAs desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="text-xs text-[#143463] font-medium hover:text-[#004FA4] flex items-center gap-1.5 transition-colors"
            >
              <i className="fa-sharp fa-solid fa-phone text-[#004FA4]" aria-hidden="true"></i>
              <span>{companyInfo.phone}</span>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
            >
              <span>Indhent tilbud</span>
              <i className="fa-sharp fa-solid fa-arrow-right text-[10px]" aria-hidden="true"></i>
            </Link>
          </div>

          {/* Mobile hamburger button */}
          <div className="flex items-center gap-2 lg:hidden">
            <a
              href={`tel:${companyInfo.phoneFormatted}`}
              className="p-2.5 text-[#004FA4] hover:bg-[#F3F4F7]"
              aria-label="Ring til Hovendals"
            >
              <i className="fa-sharp fa-solid fa-phone" aria-hidden="true"></i>
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2.5 text-[#143463] hover:bg-[#F3F4F7] focus:outline-none"
              aria-label="Åbn menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-sharp fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-[#F3F4F7] bg-white px-4 pt-3 pb-6 max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className="px-3 py-2.5 text-base font-light text-[#143463] hover:text-[#004FA4]"
              >
                Hjem
              </Link>

              <div className="py-2">
                <div className="px-3 py-1 text-xs font-semibold tracking-wider text-[#4B515D] uppercase flex items-center justify-between">
                  <span>Erhverv & privat</span>
                  <Link to="/erhverv-privat" className="text-[#004FA4] text-xs font-medium">
                    Se oversigt &rarr;
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 pl-2">
                  <Link
                    to="/hegn"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-fence text-[#004FA4]" aria-hidden="true"></i>
                    <span>Hegn</span>
                  </Link>
                  <Link
                    to="/stoejhegn"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-shield-halved text-[#004FA4]" aria-hidden="true"></i>
                    <span>Støjhegn</span>
                  </Link>
                  <Link
                    to="/laager"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-door-open text-[#004FA4]" aria-hidden="true"></i>
                    <span>Låger</span>
                  </Link>
                  <Link
                    to="/porte"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-warehouse text-[#004FA4]" aria-hidden="true"></i>
                    <span>Porte</span>
                  </Link>
                  <Link
                    to="/dobbeltporte"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-table-columns text-[#004FA4]" aria-hidden="true"></i>
                    <span>Dobbeltporte</span>
                  </Link>
                  <Link
                    to="/terrasser"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-layer-group text-[#004FA4]" aria-hidden="true"></i>
                    <span>Terrasser</span>
                  </Link>
                  <Link
                    to="/traeterrasse"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-tree text-[#004FA4]" aria-hidden="true"></i>
                    <span>Træterrasse</span>
                  </Link>
                  <Link
                    to="/kompositterrasse"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-layer-group text-[#004FA4]" aria-hidden="true"></i>
                    <span>Kompositterrasse</span>
                  </Link>
                  <Link
                    to="/carport"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-car text-[#004FA4]" aria-hidden="true"></i>
                    <span>Carport</span>
                  </Link>
                  <Link
                    to="/skur"
                    className="p-2 bg-[#F3F4F7] text-xs font-medium text-[#143463] flex items-center gap-2"
                  >
                    <i className="fa-sharp fa-solid fa-shed text-[#004FA4]" aria-hidden="true"></i>
                    <span>Skur</span>
                  </Link>
                </div>
              </div>

              <Link
                to="/om-hovendals"
                className="px-3 py-2 text-sm text-[#4B515D] hover:text-[#004FA4]"
              >
                Om Hovendals
              </Link>
              <Link
                to="/projekter"
                className="px-3 py-2 text-sm text-[#4B515D] hover:text-[#004FA4]"
              >
                Projekter
              </Link>
              <Link
                to="/referencer"
                className="px-3 py-2 text-sm text-[#4B515D] hover:text-[#004FA4]"
              >
                Referencer
              </Link>
              <Link
                to="/kontakt"
                className="px-3 py-2 text-sm text-[#4B515D] hover:text-[#004FA4]"
              >
                Kontakt
              </Link>

              <div className="pt-4 border-t border-[#F3F4F7] flex flex-col gap-2">
                <Link
                  to="/kontakt"
                  className="w-full text-center py-3 bg-[#004FA4] hover:bg-[#183E7F] text-white font-medium text-xs tracking-wider uppercase transition-colors"
                >
                  Indhent tilbud
                </Link>
                <a
                  href={`tel:${companyInfo.phoneFormatted}`}
                  className="w-full text-center py-2.5 border border-[#D6D3CE] text-[#143463] font-medium text-xs tracking-wider uppercase"
                >
                  Ring {companyInfo.phone}
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
